// src/lib/team/time-off.ts
import { formatDays, formatHours, type TeamMember } from "@/lib/team/metrics";
import { addDays, dayOfWeek, type IsoDate, monthEndOf, monthStartOf } from "@/lib/time/dates";

/** The My Team "Time off" timeline: booked (approved) PTO per person per day (TEAM-05). */

export type OffKind = "vacation" | "sick" | "holiday" | "other";
/** @public */
export const OFF_KINDS: readonly OffKind[] = ["vacation", "sick", "holiday", "other"];
/** @public */
export const OFF_KIND_LABELS: Record<OffKind, string> = {
  vacation: "Vacation",
  sick: "Sick",
  holiday: "Holiday",
  other: "Other",
};

/** One `ed-team-time-off` row: a person's booked non-productive hours of one type on one day. */
export interface TimeOffRow {
  personId: string;
  date: IsoDate;
  typeEnum: string;
  typeName: string;
  hours: number;
  description: string | null;
  /** True for a day of a still-pending request: drawn outlined, not counted as days out. */
  pending?: boolean;
  /** With `pending`: the request is approved but booking did not finish ("Approved, not booked yet"). */
  unbooked?: boolean;
}

/** @public */
export interface TimelineDay {
  date: IsoDate;
  dow: string;
  day: number;
  weekend: boolean;
  today: boolean;
}

/** @public */
export interface TimelineCell {
  date: IsoDate;
  kind: OffKind | null;
  weekend: boolean;
  today: boolean;
  /** Outlined cell: the day belongs to a pending request, not booked PTO. */
  pending: boolean;
  title: string;
}

/** @public */
export interface TimelineRow {
  personId: string;
  personName: string;
  daysOut: number;
  daysLabel: string;
  cells: TimelineCell[];
}

export interface Timeline {
  days: TimelineDay[];
  rows: TimelineRow[];
  kinds: OffKind[];
  hasPending: boolean;
}

/** A PTO request as the timeline needs it, to be spread over its weekdays. */
export interface PendingRequestSpan {
  personId: string;
  typeEnum: string;
  typeName: string;
  startDate: IsoDate;
  endDate: IsoDate;
  hoursPerDay: number;
  description: string | null;
  /** Approved but not fully booked: drawn like pending, tooltip says so. */
  unbooked?: boolean;
}

/**
 * One outlined row per weekday of each request, clamped to the month of `monthStart`
 * (weekends are never PTO days; a request with start after end yields nothing).
 */
export function expandPending(requests: PendingRequestSpan[], monthStart: IsoDate): TimeOffRow[] {
  const first = monthStartOf(monthStart);
  const last = monthEndOf(first);
  const out: TimeOffRow[] = [];
  for (const r of requests) {
    const from = r.startDate > first ? r.startDate : first;
    const to = r.endDate < last ? r.endDate : last;
    for (let d = from; d <= to; d = addDays(d, 1)) {
      const dow = dayOfWeek(d);
      if (dow === 0 || dow === 6) continue;
      out.push({
        personId: r.personId,
        date: d,
        typeEnum: r.typeEnum,
        typeName: r.typeName,
        hours: r.hoursPerDay,
        description: r.description,
        pending: true,
        ...(r.unbooked ? { unbooked: true } : {}),
      });
    }
  }
  return out;
}

const DOW = ["S", "M", "T", "W", "T", "F", "S"];
const MONTH_DAY = new Intl.DateTimeFormat("en-US", {
  timeZone: "UTC",
  month: "short",
  day: "numeric",
});

export function offKind(typeEnum: string): OffKind {
  return typeEnum === "vacation" || typeEnum === "sick" || typeEnum === "holiday"
    ? typeEnum
    : "other";
}

/**
 * `monthStart` is normalized to the first of its month. `daysOut` assumes at most one booked row
 * per person-day and is the sum of booked hours / 8.
 */
export function buildTimeline(
  members: TeamMember[],
  rows: TimeOffRow[],
  requestedMonth: IsoDate,
  today: IsoDate,
): Timeline {
  const monthStart = monthStartOf(requestedMonth);
  const end = monthEndOf(monthStart);
  const days: TimelineDay[] = [];
  for (let d = monthStart; d <= end; d = addDays(d, 1)) {
    const dow = dayOfWeek(d);
    days.push({
      date: d,
      dow: DOW[dow] ?? "",
      day: Number(d.slice(8, 10)),
      weekend: dow === 0 || dow === 6,
      today: d === today,
    });
  }
  const byPerson = new Map<string, { total: number; byDate: Map<IsoDate, TimeOffRow> }>();
  for (const row of rows) {
    if (row.date < monthStart || row.date > end) continue;
    const entry = byPerson.get(row.personId) ?? { total: 0, byDate: new Map() };
    if (!row.pending) entry.total += row.hours;
    const current = entry.byDate.get(row.date);
    // Booked beats pending; otherwise the longer day wins.
    const better =
      !current ||
      (current.pending && !row.pending) ||
      (Boolean(current.pending) === Boolean(row.pending) && row.hours > current.hours);
    if (better) entry.byDate.set(row.date, row);
    byPerson.set(row.personId, entry);
  }
  const present = new Set<OffKind>();
  let hasPending = false;
  const timelineRows = members.map((m) => {
    const entry = byPerson.get(m.personId);
    const daysOut = Math.round(((entry?.total ?? 0) / 8) * 100) / 100;
    const cells = days.map((day) => {
      const found = entry?.byDate.get(day.date);
      const kind = found ? offKind(found.typeEnum) : null;
      if (kind) present.add(kind);
      const pending = Boolean(found?.pending);
      if (pending) hasPending = true;
      const title =
        found && kind
          ? [
              m.personName,
              OFF_KIND_LABELS[kind],
              MONTH_DAY.format(new Date(`${day.date}T00:00:00Z`)),
              pending ? (found.unbooked ? "Approved, not booked yet" : "Pending approval") : null,
              found.hours < 8 ? `${formatHours(found.hours)} hrs` : null,
              found.description,
            ]
              .filter((part): part is string => Boolean(part))
              .join(" · ")
          : "";
      return { date: day.date, kind, weekend: day.weekend, today: day.today, pending, title };
    });
    return {
      personId: m.personId,
      personName: m.personName,
      daysOut,
      daysLabel: formatDays(daysOut),
      cells,
    };
  });
  timelineRows.sort((a, b) => b.daysOut - a.daysOut || a.personName.localeCompare(b.personName));
  return { days, rows: timelineRows, kinds: OFF_KINDS.filter((k) => present.has(k)), hasPending };
}
